import { Link } from 'react-router-dom';
import { useMemo, useState, useCallback } from 'react';
import posthog from 'posthog-js';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import type { LucideIcon } from 'lucide-react';
import {
  User,
  CheckCircle2,
  AlertCircle,
  Briefcase,
  FileText,
  MapPin,
  GraduationCap,
  Award,
  Phone,
  Mail,
  Link2,
  Settings,
  LogOut,
  Bell,
  ArrowLeft,
  Star,
  UploadCloud,
} from 'lucide-react';
import { Label } from '../ui/label';
import type { ProfileSectionSlug } from '../../types/profile';
import { useCandidateProfileQuery, useCandidateProfileScore } from '../../hooks/useCandidateProfile';
import { parseUploadedFileMeta } from '../../utils/profile';
import {
  exportCandidateProfilePdf,
  type CandidateProfileRecommendation,
  type CandidateProfileSectionEntry,
} from '../../api/candidate';

const sectionTargets: Record<ProfileSectionSlug, number> = {
  'personal-info': 1,
  'work-experience': 2,
  education: 1,
  skills: 10,
  certifications: 1,
  'portfolio-links': 1,
};

type SectionMeta = {
  name: string;
  slug: ProfileSectionSlug;
  icon: LucideIcon;
  entries: CandidateProfileSectionEntry[];
  score: number;
  completed: boolean;
  highlights?: string[];
  weight?: number;
  missingFields?: string[];
};

const sectionLabels: Record<ProfileSectionSlug, string> = {
  'personal-info': 'Personal Info',
  'work-experience': 'Work Experience',
  education: 'Education',
  skills: 'Skills',
  certifications: 'Certifications',
  'portfolio-links': 'Portfolio & Links',
};

const sectionIcons: Record<ProfileSectionSlug, LucideIcon> = {
  'personal-info': User,
  'work-experience': Briefcase,
  education: GraduationCap,
  skills: Award,
  certifications: Star,
  'portfolio-links': Link2,
};

export function ProfileStrength() {
  const { data: profileData = {}, isPending, isError, error } = useCandidateProfileQuery();
  const {
    data: scorecard,
    isPending: scorePending,
    isError: scoreError,
  } = useCandidateProfileScore();
  const [isExporting, setIsExporting] = useState(false);

  const baseSections = useMemo<SectionMeta[]>(() => {
    return (Object.keys(sectionTargets) as ProfileSectionSlug[]).map((slug) => {
      const entries = (profileData[slug] ?? []) as CandidateProfileSectionEntry[];
      const target = sectionTargets[slug] ?? 1;
      const score =
        target === 0 ? 100 : Math.min(100, Math.round((entries.length / target) * 100));
      const completed = entries.length >= Math.min(target, 1);
      return {
        name: sectionLabels[slug],
        slug,
        icon: sectionIcons[slug],
        entries,
        score,
        completed,
      };
    });
  }, [profileData]);

  const sections = useMemo<SectionMeta[]>(() => {
    if (!scorecard?.sections?.length) {
      return baseSections;
    }
    const map = new Map(scorecard.sections.map((section) => [section.slug, section]));
    const merged = baseSections.map((section) => {
      const apiSection = map.get(section.slug);
      if (!apiSection) return section;
      return {
        ...section,
        score: apiSection.score,
        completed: apiSection.completed,
        highlights: apiSection.highlights,
        missingFields: apiSection.missingFields,
        weight: apiSection.weight,
      };
    });
    scorecard.sections.forEach((apiSection) => {
      if (!merged.find((section) => section.slug === apiSection.slug)) {
        merged.push({
          name: sectionLabels[apiSection.slug],
          slug: apiSection.slug,
          icon: sectionIcons[apiSection.slug],
          entries: [],
          score: apiSection.score,
          completed: apiSection.completed,
          highlights: apiSection.highlights,
          missingFields: apiSection.missingFields,
          weight: apiSection.weight,
        });
      }
    });
    return merged;
  }, [baseSections, scorecard]);

  const fallbackCompletion =
    baseSections.length > 0
      ? Math.round(baseSections.reduce((sum, section) => sum + section.score, 0) / baseSections.length)
      : 0;
  const profileCompletion = scorecard?.score ?? fallbackCompletion;

  const personalInfo = (profileData['personal-info'] ?? [])[0] ?? {};
  const resumeMeta = parseUploadedFileMeta(personalInfo?.resumeUrl);
  const resumeStatus = resumeMeta?.scanStatus ?? 'clean';

  const fallbackRecommendations = useMemo<CandidateProfileRecommendation[]>(
    () => {
      const items: CandidateProfileRecommendation[] = [];
      if (!resumeMeta || resumeStatus === 'blocked') {
        items.push({
          id: 'upload-cv',
          title: resumeMeta ? 'Replace your blocked CV' : 'Upload your CV',
          description: resumeMeta
            ? 'The last upload was blocked by security checks. Please provide a fresh version.'
            : 'Applications with attached CVs get reviewed 2x faster.',
          impact: 'high',
          sectionSlug: 'personal-info',
        });
      } else if (resumeStatus === 'pending') {
        items.push({
          id: 'cv-scan',
          title: 'Hold tight while we scan your CV',
          description: 'Security scans take under a minute. We’ll unlock downloads once it’s approved.',
          impact: 'medium',
          sectionSlug: 'personal-info',
        });
      }
      sections.forEach((section) => {
        if (!section.completed) {
          items.push({
            id: `complete-${section.slug}`,
            title: `Complete ${section.name}`,
            description:
              section.slug === 'skills'
                ? 'Add at least 10 skills to boost your match score.'
                : section.slug === 'work-experience'
                ? 'Share recent achievements to prove your impact.'
                : section.slug === 'portfolio-links'
                ? 'Link to presentations, repos, or public speaking clips.'
                : 'Fill in this section to strengthen your profile.',
            impact: section.slug === 'skills' || section.slug === 'work-experience' ? 'high' : 'medium',
            sectionSlug: section.slug,
          });
        }
      });
      return items.slice(0, 4);
    },
    [resumeMeta, resumeStatus, sections],
  );

  const recommendations: CandidateProfileRecommendation[] = scorecard?.recommendations?.length
    ? scorecard.recommendations
    : fallbackRecommendations;

  const summaryText =
    scorecard?.summary ??
    (profileCompletion < 50 ? 'Getting started' : profileCompletion < 80 ? 'Almost there!' : 'Great job!');
  const percentileCopy = scorecard?.percentile
    ? `Top ${scorecard.percentile}% of candidates`
    : `${Math.max(0, 100 - profileCompletion)}% to complete`;
  const profileBadges = scorecard?.badges ?? [];

  const handleExportProfile = useCallback(async () => {
    if (typeof window === 'undefined') return;
    try {
      setIsExporting(true);
      const exportResult = await exportCandidateProfilePdf();
      const blobUrl = URL.createObjectURL(exportResult.blob);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = exportResult.fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(blobUrl);
      posthog.capture('candidate_profile_export', {
        score: scorecard?.score ?? profileCompletion,
        mimeType: exportResult.mimeType,
      });
      toast.success(
        exportResult.mimeType === 'application/pdf'
          ? 'Profile PDF downloaded.'
          : 'Profile export ready (dev fallback).',
      );
    } catch (exportError) {
      console.error(exportError);
      toast.error('Unable to export your profile right now.');
    } finally {
      setIsExporting(false);
    }
  }, [profileCompletion, scorecard?.score]);

  return (
    <div className="relative min-h-screen overflow-hidden bg-[radial-gradient(circle_at_6%_10%,rgba(99,102,241,0.18),transparent_52%),linear-gradient(180deg,#030712_0%,#020816_70%,#01020a_100%)]">
      {/* Header */}
      <header className="border-b border-white/10 bg-[rgba(5,9,19,0.85)] backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gold-gradient flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-black" />
              </div>
              <span className="text-gold-gradient">
                Lighthouse Jobs
              </span>
            </Link>
            
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" className="relative text-white">
                <Bell className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#ba9036] rounded-full" />
              </Button>
              <Link to="/life">
                <Button variant="outline" className="border-[#d2ac47]/50 text-[#ba9036] hover:bg-[#ba9036]/10">
                  Life Platform
                </Button>
              </Link>
              <Link to="/candidate/settings">
                <Button variant="ghost" size="icon" className="text-white">
                  <Settings className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/">
                <Button variant="ghost" size="icon" className="text-white">
                  <LogOut className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Link to="/candidate/dashboard">
            <Button variant="ghost" className="text-gray-400 hover:text-white mb-4 -ml-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
          <h1 className="mb-2 text-gold-gradient">
            Profile Strength
          </h1>
          <p className="text-gray-400">Complete your profile to attract more employers</p>
        </div>

        {/* Overall Progress */}
        <Card className="bg-white/5 border-white/10 mb-8">
          <CardContent className="p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-white">Profile Strength</h2>
                  {scorecard?.level && (
                    <Badge className="uppercase tracking-wide bg-[#ba9036]/20 text-[#f7ef8a] border-[#d2ac47]/40">
                      {scorecard.level}
                    </Badge>
                  )}
                </div>
                <p className="text-gray-400 text-sm">{summaryText}</p>
                {scoreError && (
                  <p className="text-xs text-yellow-400 mt-2">
                    Live scoring temporarily unavailable - showing local estimate.
                  </p>
                )}
              </div>
              <div className="text-right">
                <div className="text-white mb-1 text-4xl font-semibold">
                  {scorePending && !scorecard ? <span className="animate-pulse text-gray-400">--</span> : `${profileCompletion}%`}
                </div>
                <p className="text-xs text-gray-400">{percentileCopy}</p>
              </div>
            </div>
            <Progress value={profileCompletion} className="h-3 bg-white/10" />
            <p className="text-sm text-[#ba9036] mt-4">
              Profiles above 90% get 3x more interview requests!
            </p>
            {profileBadges.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {profileBadges.map((badge) => (
                  <Badge key={badge.id} className="bg-white/10 border-white/20 text-white">
                    {badge.label}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Section Breakdown */}
          <Card className="role-surface-candidate bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Section Breakdown</CardTitle>
              <CardDescription className="text-gray-400">
                Complete each section to improve your profile
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {scorePending && !scorecard ? (
                <div className="space-y-3">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div key={`section-skeleton-${index}`} className="h-16 rounded-lg bg-white/5 border border-white/10 animate-pulse" />
                  ))}
                </div>
              ) : (
                sections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <Link
                      key={section.slug}
                      to={`/candidate/profile-section/${section.slug}`}
                      state={{ sectionSlug: section.slug }}
                      className="block group"
                    >
                      <div className="p-4 bg-white/5 rounded-lg border border-white/10 transition-all group-hover:border-[#d2ac47]/50 group-hover:shadow-[0_0_30px_rgba(210,172,71,0.25)]">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-gold-gradient flex items-center justify-center">
                              <Icon className="w-5 h-5 text-black" />
                            </div>
                            <div>
                              <p className="text-white">{section.name}</p>
                              <p className="text-xs text-gray-400">{section.score}% complete</p>
                            </div>
                          </div>
                          {section.completed ? (
                            <CheckCircle2 className="w-5 h-5 text-green-400" />
                          ) : (
                            <AlertCircle className="w-5 h-5 text-[#ba9036]" />
                          )}
                        </div>
                        <Progress value={section.score} className="h-2 bg-white/10" />
                        {section.highlights?.length ? (
                          <p className="mt-2 text-xs text-gray-400">
                            {section.highlights.filter(Boolean).join(' • ')}
                          </p>
                        ) : null}
                        {section.missingFields?.length ? (
                          <p className="mt-1 text-xs text-[#ba9036]">{section.missingFields[0]}</p>
                        ) : null}
                      </div>
                    </Link>
                  );
                })
              )}
            </CardContent>
          </Card>

          <Card className="bg-white/5 border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Recommended Actions</CardTitle>
              <CardDescription className="text-gray-400">
                Quick wins to boost your profile visibility
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {recommendations.length === 0 ? (
                <p className="text-sm text-gray-400">You're all set! Keep your profile fresh.</p>
              ) : (
                recommendations.map((rec) => (
                  <div
                    key={rec.id}
                    className="p-4 bg-white/5 rounded-lg border border-white/10 hover:border-[#d2ac47]/50 transition-all"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-white text-sm">{rec.title}</p>
                          <Badge
                            className={
                              rec.impact === 'high'
                                ? 'bg-[#ba9036]/20 text-[#ba9036] border-[#d2ac47]/50'
                                : 'bg-blue-500/20 text-blue-400 border-blue-500/50'
                            }
                          >
                            {rec.impact} impact
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-400">{rec.description}</p>
                      </div>
                      {rec.sectionSlug && (
                        <Link
                          to={`/candidate/profile-section/${rec.sectionSlug}`}
                          className="text-xs text-[#ba9036] hover:text-[#f7ef8a]"
                        >
                          Update
                        </Link>
                      )}
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Profile Snapshot</CardTitle>
            <CardDescription className="text-gray-400">
              Quickly review the details employers can see
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {isPending ? (
              <div className="space-y-4">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={`profile-snapshot-${index}`} className="space-y-2">
                    <div className="h-3 w-24 rounded bg-white/10 animate-pulse" />
                    <div className="h-4 w-3/4 rounded bg-white/10 animate-pulse" />
                  </div>
                ))}
              </div>
            ) : isError ? (
              <p className="text-sm text-red-300">
                {error instanceof Error ? error.message : 'Unable to load profile data.'}
              </p>
            ) : (
              <>
                <div>
                  <Label className="text-white">Full Name</Label>
                  <p className="mt-1 text-gray-200">{personalInfo.fullName || 'Not provided'}</p>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label className="text-white flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      Email
                    </Label>
                    <p className="mt-1 text-gray-200 break-words">
                      {personalInfo.email || 'Add an email'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-white flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-400" />
                      Phone
                    </Label>
                    <p className="mt-1 text-gray-200">{personalInfo.phone || 'Add a phone number'}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-white flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    Location
                  </Label>
                  <p className="mt-1 text-gray-200">{personalInfo.location || 'Add your location'}</p>
                </div>
                <div>
                  <Label className="text-white">Professional Summary</Label>
                  <p className="mt-1 text-gray-300 text-sm">
                    {personalInfo.summary || 'Describe your mission, skills, and return-to-work goals.'}
                  </p>
                </div>
                <div>
                  <Label className="text-white flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-400" />
                    CV / Resume
                  </Label>
                  {resumeMeta ? (
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-3">
                      <div>
                        <p className="text-sm text-white">{resumeMeta.fileName}</p>
                        <p className="text-xs text-gray-400">
                          Uploaded{' '}
                          {resumeMeta.uploadedAt
                            ? new Date(resumeMeta.uploadedAt).toLocaleDateString()
                            : ''}
                        </p>
                        <p
                          className={`text-xs ${
                            resumeStatus === 'clean'
                              ? 'text-emerald-300'
                              : resumeStatus === 'pending'
                              ? 'text-amber-300'
                              : 'text-red-300'
                          }`}
                        >
                          {resumeStatus === 'clean'
                            ? 'Ready to share'
                            : resumeStatus === 'pending'
                            ? 'Security scan in progress'
                            : 'Blocked — upload a new CV'}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          asChild
                          variant="outline"
                          className="border-white/20 text-white hover:bg-white/5"
                          disabled={resumeStatus !== 'clean'}
                        >
                          <a
                            href={resumeStatus !== 'clean' ? undefined : resumeMeta.fileUrl}
                            target="_blank"
                            rel="noreferrer"
                            aria-disabled={resumeStatus !== 'clean'}
                          >
                            Download CV
                          </a>
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleExportProfile}
                          disabled={isExporting}
                          className="border-[#d2ac47]/50 text-[#ba9036] hover:bg-[#ba9036]/10"
                        >
                          {isExporting ? 'Preparing export...' : 'Export profile PDF'}
                        </Button>
                      </div>
                      {resumeStatus !== 'clean' && (
                        <p className="text-xs text-gray-400 mt-2">
                          {resumeStatus === 'pending'
                            ? 'Security scans usually finish within a minute.'
                            : 'Upload a new CV to replace the blocked file.'}
                        </p>
                      )}
                    </div>
                  ) : (
                    <>
                      <p className="mt-2 flex items-center gap-2 text-sm text-gray-400">
                        <UploadCloud className="w-4 h-4" />
                        Upload your CV to help employers review your experience faster.
                      </p>
                      <div className="mt-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleExportProfile}
                          disabled={isExporting}
                          className="border-[#d2ac47]/50 text-[#ba9036] hover:bg-[#ba9036]/10"
                        >
                          {isExporting ? 'Preparing export...' : 'Export profile PDF'}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
                <Button
                  asChild
                  className="bg-gold-gradient text-black hover:brightness-110"
                >
                  <Link to="/candidate/profile-section/personal-info">Edit Personal Info</Link>
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
